import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Navbar from "../components/storyforge/Navbar";
import { api, setAuth } from "../lib/api";
import { SF } from "../constants/testIds";
import { toast } from "sonner";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const SIGNUP_META = {
    title: "Sign up — Athena's Atlas | Create a personalized children's book",
    description: "Create your Athena's Atlas account to make a one-of-a-kind 24-page hardcover storybook starring your child. Free US shipping, arrives in 7–10 days.",
};
const LOGIN_META = {
    title: "Log in — Athena's Atlas",
    description: "Log in to Athena's Atlas to track your personalized children's book order, download the print-ready PDF, or start a sequel adventure.",
};

export default function AuthPage({ mode = "login" }) {
    const navigate = useNavigate();
    const isSignup = mode === "signup";
    useDocumentMeta(isSignup ? SIGNUP_META : LOGIN_META);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [fullName, setFullName] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const url = isSignup ? "/auth/signup" : "/auth/login";
            const body = isSignup ? { email, password, full_name: fullName } : { email, password };
            const { data } = await api.post(url, body);
            setAuth(data.user);
            toast.success(isSignup ? "Welcome to Athena's Atlas!" : "Welcome back!");
            navigate("/dashboard");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Something went wrong";
            setErr(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen paper-bg">
            <Navbar />
            <div className="max-w-md mx-auto px-6 pt-16 pb-24 page-in">
                <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">{isSignup ? "Begin a story" : "Welcome back"}</p>
                <h1 className="font-serif text-4xl md:text-5xl text-primary tracking-tight mb-10">
                    {isSignup ? "Create your account" : "Log in"}
                </h1>

                <form onSubmit={submit} className="space-y-5">
                    {isSignup && (
                        <div>
                            <Label htmlFor="full_name" className="text-sm">Full name</Label>
                            <Input
                                id="full_name"
                                data-testid={SF.authFullName}
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                required
                                className="mt-2 h-12 bg-muted/40 border-border"
                            />
                        </div>
                    )}
                    <div>
                        <Label htmlFor="email" className="text-sm">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            data-testid={SF.authEmail}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="mt-2 h-12 bg-muted/40 border-border"
                        />
                    </div>
                    <div>
                        <Label htmlFor="password" className="text-sm">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            data-testid={SF.authPassword}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="mt-2 h-12 bg-muted/40 border-border"
                        />
                    </div>

                    {err && (
                        <div data-testid={SF.authError} className="text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-lg">
                            {err}
                        </div>
                    )}

                    <Button
                        type="submit"
                        data-testid={SF.authSubmit}
                        disabled={loading}
                        className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl"
                    >
                        {loading ? "Please wait..." : isSignup ? "Create account" : "Log in"}
                    </Button>
                </form>

                <p className="mt-8 text-sm text-muted-foreground text-center">
                    {isSignup ? "Already have an account?" : "New to Athena's Atlas?"}{" "}
                    <Link
                        to={isSignup ? "/login" : "/signup"}
                        data-testid={SF.authToggle}
                        className="text-primary underline underline-offset-4"
                    >
                        {isSignup ? "Log in" : "Create one"}
                    </Link>
                </p>

                {/* Trust + value-prop content (helps SEO word count + reassures customer) */}
                <section className="mt-16 pt-10 border-t border-border">
                    <h2 className="font-serif text-2xl text-primary mb-6">
                        {isSignup ? "What you get with Athena's Atlas" : "Welcome back to your library"}
                    </h2>
                    <ul className="space-y-4 text-sm text-foreground/85 leading-relaxed">
                        <li className="flex gap-3">
                            <span className="text-accent mt-1">✦</span>
                            <span>
                                <strong className="text-primary">A 24-page hardcover storybook</strong> written and illustrated just for your child. Printed on heavy paper, square format (8.5″ × 8.5″), built to be read every night for years.
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="text-accent mt-1">✦</span>
                            <span>
                                <strong className="text-primary">Character that looks like them.</strong> Upload an optional photo and our AI matches their hair, skin tone, and outfit on every page — consistent from cover to back.
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="text-accent mt-1">✦</span>
                            <span>
                                <strong className="text-primary">$45 with free US shipping.</strong> Arrives in 7–10 days. No subscription, no upsells. Sequels reuse the same character at 20% off — perfect for birthdays, holidays, or starting a tradition.
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="text-accent mt-1">✦</span>
                            <span>
                                <strong className="text-primary">Private and secure.</strong> Photos are used only to create the illustrations and never shared. Your account, stories, and order history are visible only to you.
                            </span>
                        </li>
                    </ul>
                    <p className="mt-6 text-xs text-muted-foreground italic">
                        Questions before you sign up? Email us at{" "}
                        <a href="mailto:hello@athenasatlas.com" className="underline underline-offset-2">hello@athenasatlas.com</a>{" "}
                        and a real human will reply within a day.
                    </p>
                </section>
            </div>
        </div>
    );
}
